import React, { useState } from "react"
import { __snackBarSeverity } from "../../constants"
import { Button, Drawer, Typography, Box, Grid, Paper, Chip, TextField } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { styled } from "@mui/system"
import moment from "moment"
import { resolveDisputeController } from "../../Controllers/InvoiceDisputes"
import mainContext from "../../contexts/mainContext"
import { formatWithTZ } from "../../utils" // Import formatWithTZ

const anchor = "right" // Direction from where drawer will open

export default function DisputeDetail({ open, handleClose, data, cb, index }) {
  const { setSnackBar } = React.useContext(mainContext)
  const [adminRemark, setAdminRemark] = useState("")
  const [remarkError, setRemarkError] = useState(false)

  const handleResolve = async (status) => {
    // Validate admin remark is provided
    if (!adminRemark.trim()) {
      setRemarkError(true)
      setSnackBar({
        status: true,
        text: "Admin remark is required to resolve the dispute",
        severity: __snackBarSeverity.error,
      })
      return
    }

    const res = await resolveDisputeController(data.invoiceNo, {
      status,
      adminRemark: adminRemark.trim(),
    })

    if (res.data.status === 200) {
      setSnackBar({
        status: true,
        text: `Dispute ${status.toLowerCase()} successfully`,
        severity: __snackBarSeverity.success,
      })
      // Update the parent component's state
      cb(status, data.createdAt, index, adminRemark.trim())

      setAdminRemark("")
      handleClose()
    } else {
      setSnackBar({
        status: true,
        text: res.data.errors[0].message,
        severity: __snackBarSeverity.error,
      })
    }
  }

  const formatCurrency = (amount) => {
    return `$${Number.parseFloat(amount || 0).toFixed(2)}`
  }

  // Add a function to display multiple dispute reasons if both parties raised disputes
  const getDisputeReasons = (dispute) => {
    if (!dispute.dispute || !dispute.dispute.raisedBy || dispute.dispute.raisedBy.length === 0) {
      return <Typography variant="body2">No dispute information available</Typography>
    }

    return dispute.dispute.raisedBy.map((raiser, idx) => (
      <Paper
        key={idx}
        elevation={1}
        sx={{ p: 2, mb: 2, borderLeft: `4px solid ${raiser.role === "Seeker" ? "#eeb08c" : "#6ba9db"}` }}
      >
        <Typography
          variant="subtitle2"
          color={raiser.role === "Seeker" ? "var(--nona-theme-color)" : "var(--nona-light-blue)"}
        >
          Raised by: {raiser.role === "Seeker" ? "Customer" : raiser.role}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Date: {formatWithTZ(raiser.raisedOn, "DD MMM, YYYY HH:mm")}
        </Typography>
        <Typography variant="subtitle2" mt={1}>
          Reason:
        </Typography>
        <Typography variant="body2">{raiser.reason || "No reason provided"}</Typography>
      </Paper>
    ))
  }

  return (
    <Drawer anchor={anchor} open={open} onClose={handleClose} sx={{ height: "100vh" }}>
      <Container>
        <Header>
          <div>
            <h4>Disputed Invoice's Details</h4>
            <h5 style={{ margin: "5px 0", color: "var(--nona-grey)" }}>{data.invoiceNo}</h5>
          </div>
          <CloseIcon className="pointer" onClick={handleClose} />
        </Header>

        {data && data.invoiceInfo && (
          <Body>
            <Section>
              <SectionTitle>Parties</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper elevation={1} sx={{ p: 2, borderTop: "3px solid var(--nona-theme-color)" }}>
                    <Typography variant="subtitle2" color="var(--nona-theme-color)" fontWeight="600">
                      Payer Info (Customer)
                    </Typography>
                    <Typography fontWeight="500">{`${data.invoiceInfo.payBy.firstName || ""} ${data.invoiceInfo.payBy.lastName || ""}`}</Typography>
                    <Typography variant="body2">{data.invoiceInfo.payBy.email}</Typography>
                    <Typography variant="body2">Role: {data.invoiceInfo.payBy.role}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper elevation={1} sx={{ p: 2, borderTop: "3px solid var(--nona-light-blue)" }}>
                    <Typography variant="subtitle2" color="var(--nona-light-blue)" fontWeight="600">
                      Payee Info (Provider)
                    </Typography>
                    <Typography fontWeight="500">{`${data.invoiceInfo.payTo.firstName || ""} ${data.invoiceInfo.payTo.lastName || ""}`}</Typography>
                    <Typography variant="body2">{data.invoiceInfo.payTo.email}</Typography>
                    <Typography variant="body2">Role: {data.invoiceInfo.payTo.role}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Section>

            {/* Initial Job Duration - from response.jobId.availability */}
            {data?.jobId?.availability && data.jobId.availability.length > 0 && (
              <Section>
                <SectionTitle>Initial Job Duration (Customer's Original Request)</SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This is the original availability requested by the customer when booking the job
                </Typography>
                <Grid container spacing={2}>
                  {data?.jobId.availability.map((avail, idx) => {
                    const firstSlot = avail.availabilityTime?.[0] // Get first slot for dates
                    return (
                      <Grid item xs={12} key={idx}>
                        <Paper
                          elevation={1}
                          sx={{ p: 2, mb: 2, borderRadius: "8px", borderLeft: "4px solid var(--nona-theme-color)" }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: "var(--nona-theme-color)" }}>
                              {firstSlot 
                                ? `${formatWithTZ(firstSlot.startUTCTime, "DD MMM, YYYY")} - ${formatWithTZ(firstSlot.endUTCTime, "DD MMM, YYYY")}`
                                : "Date not available"
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                              |
                            </Typography>
                            {avail.availabilityTime.map((time, timeIdx) => (
                              <Paper
                                key={timeIdx}
                                elevation={0}
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  bgcolor: "#fff5f5",
                                  borderRadius: "16px",
                                  border: "1px solid var(--nona-theme-color)",
                                  display: "inline-block",
                                }}
                              >
                                <Typography variant="body2" color="var(--nona-theme-color)" fontWeight="500">
                                  {time.startUTCTime && time.endUTCTime
                                    ? `${formatWithTZ(time.startUTCTime)} - ${formatWithTZ(time.endUTCTime)}`
                                    : `${time.startTime || "N/A"} - ${time.endTime || "N/A"}`
                                  }
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )
                  })}
                </Grid>
              </Section>
            )}

            {/* Provider's Modified Availability - from invoiceInfo.availability */}
            {data.invoiceInfo.availability && data.invoiceInfo.availability.length > 0 && (
              <Section>
                <SectionTitle>
                  {data.invoiceInfo.billDetail.meta.hourAdjustment.updatedHour
                    ? "Updated Job Duration (Modified by Provider)"
                    : "Job Duration"}
                </SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {data.invoiceInfo.billDetail.meta.penalty &&
                  Object.keys(data.invoiceInfo.billDetail.meta.penalty).length > 0
                    ? "Provider modified the original schedule and added additional hours"
                    : "Final job schedule as per the invoice"}
                </Typography>
                <Grid container spacing={2}>
                  {data.invoiceInfo.availability.map((avail, idx) => {
                    const firstSlot = avail.availabilityTime?.[0] // Get first slot for dates
                    return (
                      <Grid item xs={12} key={idx}>
                        <Paper
                          elevation={1}
                          sx={{ p: 2, mb: 2, borderRadius: "8px", borderLeft: "4px solid var(--nona-light-blue)" }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: "var(--nona-light-blue)" }}>
                              {firstSlot 
                                ? `${formatWithTZ(firstSlot.startUTCTime, "DD MMM, YYYY")} - ${formatWithTZ(firstSlot.endUTCTime, "DD MMM, YYYY")}`
                                : "Date not available"
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                              |
                            </Typography>
                            {avail.availabilityTime.map((time, timeIdx) => (
                              <Paper
                                key={timeIdx}
                                elevation={0}
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  bgcolor: "#f0f8ff",
                                  borderRadius: "16px",
                                  border: "1px solid var(--nona-light-blue)",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2" color="var(--nona-light-blue)" fontWeight="500">
                                  {time.startUTCTime && time.endUTCTime
                                    ? `${formatWithTZ(time.startUTCTime)} - ${formatWithTZ(time.endUTCTime)}`
                                    : `${time.startTime || "N/A"} - ${time.endTime || "N/A"}`
                                  }
                                </Typography>
                                {firstSlot && moment(firstSlot.startUTCTime).isBefore(new Date()) ? (
                                  <Box sx={{ color: "#10b981", display: "flex", alignItems: "center" }}>
                                    <span style={{ fontSize: "16px" }}>✓</span>
                                  </Box>
                                ) : (
                                  <Box sx={{ color: "#f59e0b", display: "flex", alignItems: "center" }}>
                                    <span style={{ fontSize: "16px" }}>⏱</span>
                                  </Box>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )
                  })}
                </Grid>
              </Section>
            )}

            <Section>
              <SectionTitle>Payment Overview</SectionTitle>

              {/* Pre-authorized Amount Details */}
              <Box className="meta-info-box">
                <Typography className="meta-info-title">Initial Booking Amount</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Amount customer paid when initially booking the job based on original duration
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <div className="meta-info-row">
                      <span className="meta-info-label">Total Hours:</span>
                      <span className="meta-info-value">
                        {data.invoiceInfo.billDetail.meta.preAuthorizedAmount?.totalHours || "N/A"}
                      </span>
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="meta-info-row">
                      <span className="meta-info-label">Total Amount:</span>
                      <span className="meta-info-value">
                        {formatCurrency(data.invoiceInfo.billDetail.meta.preAuthorizedAmount?.totalAmount)}
                      </span>
                    </div>
                    <div className="meta-info-row">
                      <span className="meta-info-label">To Provider:</span>
                      <span className="meta-info-value">
                        {formatCurrency(data.invoiceInfo.billDetail.meta.preAuthorizedAmount?.toProvider)}
                      </span>
                    </div>
                    <div className="meta-info-row">
                      <span className="meta-info-label">Platform Fee:</span>
                      <span className="meta-info-value">
                        {formatCurrency(data.invoiceInfo.billDetail.meta.preAuthorizedAmount?.toPlatform)}
                      </span>
                    </div>
                  </Grid>
                </Grid>
              </Box>

              {/* Hour Adjustment Details - Only show if exists */}
              {data.invoiceInfo.billDetail.meta.hourAdjustment?.updatedHour &&
                (
                  <Box className="meta-info-box">
                    <Typography className="meta-info-title">Additional Hours (Added by Provider)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Provider added extra hours beyond the original booking duration
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <div className="meta-info-row">
                          <span className="meta-info-label">Extra Hours:</span>
                          <span className="meta-info-value">
                            {data.invoiceInfo.billDetail.meta.hourAdjustment.updatedHour || "N/A"}
                          </span>
                        </div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="meta-info-row">
                          <span className="meta-info-label">Additional Amount:</span>
                          <span className="meta-info-value">
                            {formatCurrency(data.invoiceInfo.billDetail.meta.hourAdjustment.totalAmount || 0)}
                          </span>
                        </div>
                        <div className="meta-info-row">
                          <span className="meta-info-label">To Provider:</span>
                          <span className="meta-info-value">
                            {formatCurrency(data.invoiceInfo.billDetail.meta.hourAdjustment.toProvider || 0)}
                          </span>
                        </div>
                        <div className="meta-info-row">
                          <span className="meta-info-label">Platform Fee:</span>
                          <span className="meta-info-value">
                            {formatCurrency(data.invoiceInfo.billDetail.meta.hourAdjustment.toPlatform || 0)}
                          </span>
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                )}

              {/* Penalty Details */}
              {data.invoiceInfo.billDetail.meta.penalty.toCustmer > 0 && (
                <Box className="meta-info-box">
                  <Typography className="meta-info-title">Penalty</Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <div className="meta-info-row">
                        <span className="meta-info-label">To Customer:</span>
                        <span className="meta-info-value">
                          {formatCurrency(data.invoiceInfo.billDetail.meta.penalty.toCustomer)}
                        </span>
                      </div>
                      <Typography variant="caption" color="textSecondary" sx={{ ml: 0 }}>
                        Penalty applied due to customer's previous cancelled booking
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Additional Payment Details */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={4}>
                  <InfoItem>
                    <Typography variant="body2">Hourly Rate:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(data.invoiceInfo.billDetail.hourlyRate)}
                    </Typography>
                  </InfoItem>
                </Grid>

                {data.invoiceInfo.billDetail.overnightRate > 0 && (
                  <Grid item xs={4}>
                    <InfoItem>
                      <Typography variant="body2">Overnight Rate:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(data.invoiceInfo.billDetail.overnightRate)}
                      </Typography>
                    </InfoItem>
                  </Grid>
                )}

                <Grid item xs={4}>
                  <InfoItem>
                    <Typography variant="body2">Extra Charges:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(data.invoiceInfo.billDetail.meta.extraCharges)}
                    </Typography>
                  </InfoItem>
                </Grid>
              </Grid>

              {data.invoiceInfo.seekerRemarks && (
                <Box mt={2}>
                  <Typography variant="subtitle2" fontWeight="600">
                    Customer Remarks:
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: "8px" }}>
                    <Typography variant="body2">{data.invoiceInfo.seekerRemarks}</Typography>
                  </Paper>
                </Box>
              )}
            </Section>

            <Section>
              <SectionTitle>Dispute Information</SectionTitle>
              {getDisputeReasons(data)}
              
              {/* Display admin remark if dispute is resolved */}
              {data.dispute.status === "Resolved"  && (
                <Box mt={2}>
                  <Typography variant="subtitle2" fontWeight="600">
                    Admin Resolution Note:
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, bgcolor: "#e8f5e9", borderRadius: "8px", borderLeft: "4px solid #4caf50" }}
                  >
                    <Typography variant="body2">{data.dispute.adminRemark}</Typography>
                  </Paper>
                </Box>
              )}
            </Section>

            {data.dispute.status === "Pending" && (
              <>
                <Section>
                  <SectionTitle>Resolution</SectionTitle>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please provide a detailed explanation of your decision to resolve this dispute. This will be visible
                    to both parties.
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Admin Resolution Note"
                    placeholder="Explain your decision for resolving this dispute..."
                    value={adminRemark}
                    onChange={(e) => {
                      setAdminRemark(e.target.value)
                      if (e.target.value.trim()) {
                        setRemarkError(false)
                      }
                    }}
                    error={remarkError}
                    helperText={remarkError ? "Admin remark is required to resolve the dispute" : ""}
                    sx={{ mb: 2 }}
                  />
                </Section>

                <ActionButtons>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleResolve("Resolved")}
                    sx={{ fontWeight: 500, width: "100%" }}
                  >
                    Resolve Dispute
                  </Button>
                </ActionButtons>
              </>
            )}
          </Body>
        )}
      </Container>
    </Drawer>
  )
}

const Container = styled("div")`
  height: 100vh;
  overflow: auto;
  width: 40vw;
  padding: 1.5rem;
  .title {
    color: var(--nona-theme-color);
    font-size: 90%;
  }
  .divider-gap {
    margin: 0.5rem 0;
  }
  .meta-info-box {
    background-color: #f9f9f9;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
    border: 1px solid #e0e0e0;
  }
  .meta-info-title {
    font-weight: 600;
    font-size: 16px;
    color: var(--nona-theme-color);
    margin-bottom: 12px;
  }
  .meta-info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    padding: 4px 0;
  }
  .meta-info-label {
    color: #666;
    font-size: 14px;
  }
  .meta-info-value {
    font-weight: 500;
    color: #333;
    font-size: 14px;
  }
  @media (max-width: 1200px) {
    width: 60vw;
  }
  @media (max-width: 768px) {
    width: 80vw;
  }
`

const Header = styled("div")`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  h4 {
    font-weight: 900;
    font-size: 120%;
    color: var(--nona-theme-color);
    margin-bottom: 5px;
  }
  top: 0;
  background-color: white;
  padding: 1rem 0;
  margin: -1.5rem -1.5rem 0 -1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  border-bottom: 1px solid var(--card-border);
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`

const Body = styled("div")`
  margin-top: 1.5rem;
  padding: 5px;
  font-size: 14px;
`

const Section = styled("div")`
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
`

const SectionTitle = styled("h3")`
  font-size: 18px;
  margin-bottom: 16px;
  color: var(--nona-theme-color);
  font-weight: 600;
`

const InfoItem = styled("div")`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 8px;
`

const ActionButtons = styled("div")`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  margin-bottom: 16px;
`